// src/components/ResourceManager/Datacenter/DcInventoryPage.tsx

import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react"; // Para obtener el token
import { columns as baseColumns, DataCenter } from "./DcData";

// Importamos las funciones del request
import {
	getDataCenters,
	addDataCenter,
	updateDataCenter,
	deleteDataCenter,
	AddDataCenterPayload,
	getProviderIds,
} from "./request/request";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";

// Icons
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

/**
 * Interfaz para los objetos que llegan del backend (GET /list).
 * Suelen tener "capacityKW" y "rackCapacity" como string, etc.
 * Ajusta los campos según lo que devuelva tu API.
 */
interface RawDataCenterAPI {
	datacenterId: string;
	name: string;
	direction: string;
	gps: string;
	capacityKW?: string; // Normalmente string, luego lo parseamos a number
	phase: string;
	cage: string;
	rackId: string;
	rackCapacity?: string;
	patchPanelODF: string;
	rackSecurityPin: string;
	status: string;
	visible: boolean;
	providerId: string;
	providerCode: string;
	city: string;
	displayName: string;
}
interface ProviderOption {
	id: number;
	name: string;
}

export default function DcInventoryPage() {
	// ========= 1) Table data  =========
	const [dataCenters, setDataCenters] = useState<DataCenter[]>([]);

	/**
	 * isFetching: usaremos esta misma variable de estado para
	 *  1) mostrar spinner durante el GET inicial
	 *  2) y también para mostrar spinner mientras ejecutamos ADD/EDIT/DELETE
	 */
	const [isFetching, setIsFetching] = useState(false);

	// ========= 2) Add/Edit modal state & fields =========
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);

	// Campos para Add/Edit
	const [dcId, setDcId] = useState(""); // datacenterId
	const [dcName, setDcName] = useState("");
	const [dcDirection, setDcDirection] = useState("");
	const [dcCapacity, setDcCapacity] = useState("");
	const [dcStatus, setDcStatus] = useState("Active");
	const [dcGps, setDcGps] = useState("");
	const [dcPhase, setDcPhase] = useState("");
	const [dcCage, setDcCage] = useState("");
	const [dcRackId, setDcRackId] = useState("");
	const [dcRackCapacity, setDcRackCapacity] = useState("");
	const [dcPatchPanelODF, setDcPatchPanelODF] = useState("");
	const [dcRackSecurityPin, setDcRackSecurityPin] = useState("");
	const [dcVisible, setDcVisible] = useState(false);
	const [dcProvider, setDcProvider] = useState("");
	const [dcProviderCode, setDcProviderCode] = useState("");
	const [dcCity, setDcCity] = useState("");
	const [dcDisplayName, setDcDisplayName] = useState("");
	const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

	// ========= 3) View modal state =========
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedDataCenter, setSelectedDataCenter] =
		useState<DataCenter | null>(null);

	// ========= 4) Delete confirmation state =========
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [dcToDelete, setDcToDelete] = useState<DataCenter | null>(null);

	// ========== Auth0 para obtener token y llamar a nuestros endpoints ==========
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

	//Provider Dropdown Handling logic
	// 1) Convert provider objects to an array of strings for the dropdown
	const providerNames = providerOptions.map((opt) => opt.name);

	// 2) Find the name associated with the currently selected provider ID
	function getProviderNameById(providerId: string): string {
		const match = providerOptions.find((opt) => String(opt.id) === providerId);
		return match ? match.name : "";
	}

	// 3) When the user picks a name, find its ID and store it
	function handleSelectProviderName(selectedName: string) {
		const match = providerOptions.find((opt) => opt.name === selectedName);
		setDcProvider(match ? String(match.id) : "");
	}

	// ------------------------------------------------------------------------------------
	// useEffect: llama GET /v1/dataCenters/list
	// ------------------------------------------------------------------------------------
	useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) return;

		const fetchDataCentersList = async () => {
			try {
				setIsFetching(true); // Activa spinner
				const token = await getAccessTokenSilently();
				const response = await getDataCenters(token);
				console.log("Data centers (raw) =>", response.data);

				// Convertimos capacityKW -> capacity (number)
				const shapedData: DataCenter[] = response.data.map(
					(item: RawDataCenterAPI) => ({
						datacenterId: item.datacenterId,
						name: item.name,
						direction: item.direction,
						gps: item.gps,
						capacity: parseFloat(item.capacityKW ?? "0"), // number
						phase: item.phase,
						cage: item.cage,
						rackId: item.rackId,
						rackCapacity: parseFloat(item.rackCapacity ?? "0"),
						patchPanelODF: item.patchPanelODF,
						rackSecurityPin: item.rackSecurityPin,
						status: item.status,
						visible: item.visible,
						providerId: item.providerId,
						providerCode: item.providerCode,
						city: item.city,
						displayName: item.displayName,
					})
				);

				setDataCenters(shapedData);
			} catch (err) {
				console.error("Error fetching data centers:", err);
			} finally {
				setIsFetching(false); // Desactiva spinner
			}
		};
		const fetchProviderOptions = async () => {
			try {
				const token = await getAccessTokenSilently();
				// getProviderIds should return an array
				const providerResp = await getProviderIds(token);
				setProviderOptions(providerResp.data); // store them in state
			} catch (error) {
				console.error("Error fetching provider IDs:", error);
			}
		};

		fetchProviderOptions();
		fetchDataCentersList();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	// ------------------------------------------------------------------------------------
	// Handle ADD
	// ------------------------------------------------------------------------------------
	const handleAddDC = () => {
		setEditMode(false);
		// Limpiar campos
		setDcId("");
		setDcName("");
		setDcDirection("");
		setDcCapacity("");
		setDcStatus("Active");
		setDcGps("");
		setDcPhase("");
		setDcCage("");
		setDcRackId("");
		setDcRackCapacity("");
		setDcPatchPanelODF("");
		setDcRackSecurityPin("");
		setIsModalOpen(true);
		setDcVisible(true);
		setDcProvider("");
		setDcProviderCode("");
		setDcCity("");
		setDcDisplayName("");
	};

	// ------------------------------------------------------------------------------------
	// Handle EDIT
	// ------------------------------------------------------------------------------------
	const handleEditDC = (dc: DataCenter) => {
		setEditMode(true);
		setDcId(dc.datacenterId);
		setDcName(dc.name);
		setDcDirection(dc.direction);
		setDcCapacity(String(dc.capacity));
		setDcStatus(dc.status);
		setDcGps(dc.gps);
		setDcPhase(dc.phase);
		setDcCage(dc.cage);
		setDcRackId(dc.rackId);
		setDcRackCapacity(String(dc.rackCapacity));
		setDcPatchPanelODF(dc.patchPanelODF);
		setDcRackSecurityPin(dc.rackSecurityPin);
		setIsModalOpen(true);
		setDcVisible(dc.visible ?? false);
		setDcProvider(dc.providerId ?? "");
		setDcProviderCode(dc.providerCode ?? "");
		setDcCity(dc.city ?? "");
		setDcDisplayName(dc.displayName ?? "");
	};

	// ------------------------------------------------------------------------------------
	// Handle SAVE (ADD / UPDATE)
	// ------------------------------------------------------------------------------------
	const handleSaveDataCenter = async () => {
		try {
			setIsFetching(true); // spinner durante ADD  UPDATE
			const token = await getAccessTokenSilently();
			const payload: AddDataCenterPayload = {
				datacenterId: dcId,
				name: dcName,
				direction: dcDirection,
				gps: dcGps,
				status: dcStatus,
				capacityKW: dcCapacity,
				phase: dcPhase,
				cage: dcCage,
				rackId: dcRackId,
				rackCapacity: dcRackCapacity,
				patchPanelODF: dcPatchPanelODF,
				rackSecurityPin: dcRackSecurityPin,
				visible: dcVisible,
				providerId: dcProvider,
				providerCode: dcProviderCode,
				city: dcCity,
				displayName: dcDisplayName,
			};
			if (editMode) {
				// Update (PUT)
				await updateDataCenter(token, dcId, payload);

				setDataCenters((prev) =>
					prev.map((item) =>
						item.datacenterId === dcId
							? {
									...item,
									name: dcName,
									direction: dcDirection,
									capacity: Number(dcCapacity) || 0,
									status: dcStatus,
									gps: dcGps,
									phase: dcPhase,
									cage: dcCage,
									rackId: dcRackId,
									rackCapacity: Number(dcRackCapacity) || 0,
									patchPanelODF: dcPatchPanelODF,
									rackSecurityPin: dcRackSecurityPin,
									visible: dcVisible,
									providerId: dcProvider,
									providerCode: dcProviderCode,
									city: dcCity,
									displayName: dcDisplayName,
								}
							: item
					)
				);
			} else {
				// Add (POST)
				await addDataCenter(token, payload);

				setDataCenters((prev) => [
					...prev,
					{
						datacenterId: dcId,
						name: dcName,
						direction: dcDirection,
						capacity: Number(dcCapacity) || 0,
						status: dcStatus,
						gps: dcGps,
						phase: dcPhase,
						cage: dcCage,
						rackId: dcRackId,
						rackCapacity: Number(dcRackCapacity) || 0,
						patchPanelODF: dcPatchPanelODF,
						rackSecurityPin: dcRackSecurityPin,
						visible: dcVisible,
						providerId: dcProvider,
						providerCode: dcProviderCode,
						city: dcCity,
						displayName: dcDisplayName,
					},
				]);
			}
		} catch (error) {
			console.error("Error saving Data Center:", error);
		} finally {
			setIsFetching(false); // spinner off
		}

		setIsModalOpen(false);
	};

	// ------------------------------------------------------------------------------------
	// Handle VIEW
	// ------------------------------------------------------------------------------------
	const handleViewDC = (dc: DataCenter) => {
		setSelectedDataCenter(dc);
		setIsViewModalOpen(true);
	};

	// ------------------------------------------------------------------------------------
	// Handle DELETE
	// ------------------------------------------------------------------------------------
	const handleDeleteClick = (dc: DataCenter) => {
		setDcToDelete(dc);
		setIsAlertOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!dcToDelete) return;
		try {
			setIsFetching(true); // spinner durante DELETE
			const token = await getAccessTokenSilently();
			await deleteDataCenter(token, dcToDelete.datacenterId);

			setDataCenters((prev) =>
				prev.filter((item) => item.datacenterId !== dcToDelete.datacenterId)
			);
			setDcToDelete(null);
			setIsAlertOpen(false);
		} catch (error) {
			console.error("Error deleting data center:", error);
		} finally {
			setIsFetching(false); // spinner off
		}
	};

	const handleCancelDelete = () => {
		setDcToDelete(null);
		setIsAlertOpen(false);
	};

	// ------------------------------------------------------------------------------------
	// Override "Actions"
	// ------------------------------------------------------------------------------------
	const columns = baseColumns.map((col) => {
		if (col.header === "Actions" && col.renderCell) {
			return {
				...col,
				renderCell: (row: DataCenter) => (
					<div className="flex items-center gap-2">
						{/* View */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleViewDC(row)}
						>
							<IconEye className="w-6 h-6" />
						</button>

						{/* Edit */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleEditDC(row)}
						>
							<IconEdit className="w-10 h-10" />
						</button>

						{/* Delete */}
						<button
							className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
							onClick={() => handleDeleteClick(row)}
						>
							<IconDelete className="w-6 h-6" />
						</button>
					</div>
				),
			};
		}
		return col;
	});

	// ------------------------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------------------------
	return (
		<div className="relative p-4">
			{/* Contenido principal siempre */}
			{/* Header */}
			<div className="mb-4 p-4 bg-[var(--semantic-bg-primary)]">
				<div className="flex items-end justify-between">
					<div className="flex items-end space-x-4"></div>
					<GenericButton
						label={
							<div className="flex items-center gap-2">
								<IconPlus className="w-4 h-4" />
								<span>Add new data center</span>
							</div>
						}
						variant="brand"
						size="md"
						onClick={handleAddDC}
					/>
				</div>
			</div>

			{/* Table */}
			<GenericTable
				columns={columns}
				data={dataCenters}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				tableClassName="bg-[var(--semantic-bg-primary)]"
				width="100%"
			/>

			{/* ====================== Modal Add / Edit ====================== */}
			<GenericModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={editMode ? "Edit Data Center" : "Add Data Center"}
				size="lg"
			>
				{/* 3-column grid */}
				<div className="grid grid-cols-3 gap-4 mb-4">
					<GenericInput
						label="DatacenterId"
						value={dcId}
						onChange={setDcId}
						readOnly={editMode} // clave no editable en modo Edit
					/>
					<GenericInput
						label="Name"
						placeholder="e.g. DC North"
						value={dcName}
						onChange={setDcName}
					/>
					<GenericInput
						label="Direction"
						placeholder="e.g. 123 North St, City"
						value={dcDirection}
						onChange={setDcDirection}
					/>
					<GenericInput
						label="Gps"
						placeholder="e.g. 40.7128° N, 74.0060° W"
						value={dcGps}
						onChange={setDcGps}
					/>
					<GenericInput
						label="CapacityKW"
						placeholder="e.g. 1000"
						value={dcCapacity}
						onChange={setDcCapacity}
					/>
					<GenericInput
						label="Phase"
						placeholder="e.g. 3-phase"
						value={dcPhase}
						onChange={setDcPhase}
					/>

					<GenericInput
						label="Cage"
						placeholder="e.g. Cage A"
						value={dcCage}
						onChange={setDcCage}
					/>
					<GenericInput
						label="RackId"
						placeholder="e.g. RACK-001"
						value={dcRackId}
						onChange={setDcRackId}
					/>
					<GenericInput
						label="RackCapacity"
						placeholder="e.g. 42"
						value={dcRackCapacity}
						onChange={setDcRackCapacity}
					/>
					<GenericInput
						label="PatchPanelODF"
						placeholder="e.g. ODF-001"
						value={dcPatchPanelODF}
						onChange={setDcPatchPanelODF}
					/>
					<GenericInput
						label="RackSecurityPin"
						placeholder="e.g. 1234"
						value={dcRackSecurityPin}
						onChange={setDcRackSecurityPin}
					/>
					<div className="flex flex-col space-y-2">
						<label className="text-sm font-medium ">Status</label>
						<GenericDropdown
							items={["Active", "Implementation", "Deprecated"]}
							value={dcStatus}
							onSelect={(newValue) => setDcStatus(newValue)}
							defaultLabel="Status"
							widthClass="w-full"
						/>
					</div>
					<div className="flex flex-col">
						<label className="text-sm font-medium">Provider</label>
						<GenericDropdown
							items={providerNames} // pass an array of strings
							value={getProviderNameById(dcProvider)} // convert ID -> name
							onSelect={handleSelectProviderName} // convert name -> ID
							defaultLabel="Select a Provider"
							widthClass="w-full"
						/>
					</div>

					<GenericInput
						label="Provider Code"
						placeholder="e.g. MEX2"
						value={dcProviderCode}
						onChange={setDcProviderCode}
					/>

					<GenericInput
						label="City"
						placeholder="e.g. Santa Fe"
						value={dcCity}
						onChange={setDcCity}
					/>

					<GenericInput
						label="Display Name"
						placeholder="Auto or e.g. MEX2 - Santa Fe"
						value={dcDisplayName}
						disabled={true}
						onChange={setDcDisplayName}
					/>
					<GenericCheckbox
						label="Make visible to end users"
						onChange={setDcVisible}
						checked={dcVisible}
					/>
				</div>

				<div className="flex justify-end space-x-2">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsModalOpen(false)}
					>
						Cancel
					</button>
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              hover:bg-opacity-90
              transition-colors
            "
						onClick={handleSaveDataCenter}
					>
						Save
					</button>
				</div>
			</GenericModal>

			{/* ====================== Modal View (read-only) ====================== */}
			<GenericModal
				isOpen={isViewModalOpen}
				onClose={() => setIsViewModalOpen(false)}
				title="Data Center Details"
				size="lg"
			>
				{selectedDataCenter && (
					<div className="grid grid-cols-3 gap-4 mb-4">
						<GenericInput
							label="DatacenterId"
							value={selectedDataCenter.datacenterId}
							readOnly
						/>
						<GenericInput
							label="Name"
							value={selectedDataCenter.name}
							readOnly
						/>
						<GenericInput
							label="Direction"
							value={selectedDataCenter.direction}
							readOnly
						/>
						<GenericInput label="Gps" value={selectedDataCenter.gps} readOnly />
						<GenericInput
							label="Capacity (KW)"
							value={String(selectedDataCenter.capacity)}
							readOnly
						/>
						<GenericInput
							label="Phase"
							value={selectedDataCenter.phase}
							readOnly
						/>
						<GenericInput
							label="Cage"
							value={selectedDataCenter.cage}
							readOnly
						/>
						<GenericInput
							label="RackId"
							value={selectedDataCenter.rackId}
							readOnly
						/>
						<GenericInput
							label="RackCapacity"
							value={String(selectedDataCenter.rackCapacity)}
							readOnly
						/>
						<GenericInput
							label="PatchPanelODF"
							value={selectedDataCenter.patchPanelODF}
							readOnly
						/>
						<GenericInput
							label="RackSecurityPin"
							value={selectedDataCenter.rackSecurityPin}
							readOnly
						/>
						<GenericInput
							label="Status"
							value={selectedDataCenter.status}
							readOnly
						/>
					</div>
				)}
				<div className="flex justify-end">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsViewModalOpen(false)}
					>
						Close
					</button>
				</div>
			</GenericModal>

			{/* ====================== ALERT: confirm Delete ====================== */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the data center from the inventory."
				cancelLabel="Cancel"
				confirmLabel="Delete"
				iconWidthClass="w-20 h-12"
			/>

			{/* Overlay semi-transparente sólo si isFetching */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}
